import { hoyLocal, toYMD } from './fechaLocal';
import type { TaskItem } from '../components/tasks/types';

/**
 * Fechas de las tareas: en qué grupo cae cada una (vencidas, hoy, mañana,
 * esta semana, después, sin fecha, completadas) y cómo se lee el vencimiento.
 * Todo en día LOCAL del navegador (las tareas se cargan desde el taller).
 */

export type GrupoKey = 'vencidas' | 'hoy' | 'manana' | 'semana' | 'despues' | 'sinFecha' | 'completadas';

export const GRUPOS: Array<{ key: GrupoKey; label: string }> = [
  { key: 'vencidas', label: 'Vencidas' },
  { key: 'hoy', label: 'Hoy' },
  { key: 'manana', label: 'Mañana' },
  { key: 'semana', label: 'Esta semana' },
  { key: 'despues', label: 'Más adelante' },
  { key: 'sinFecha', label: 'Sin fecha' },
  { key: 'completadas', label: 'Completadas' }
];

const DIAS_CORTOS = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];
const DIAS_LARGOS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];

const parseYmd = (ymd: string): Date => {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d);
};

export const sumarDias = (ymd: string, n: number): string => {
  const d = parseYmd(ymd);
  d.setDate(d.getDate() + n);
  return toYMD(d);
};

// Domingo que cierra la semana del día dado
export const finDeSemana = (ymd: string): string => {
  const d = parseYmd(ymd);
  const dow = d.getDay(); // 0 = domingo
  return sumarDias(ymd, dow === 0 ? 0 : 7 - dow);
};

// Próximo día de semana (1 = lunes ... 0 = domingo) estrictamente después de hoy
export const proximoDiaSemana = (ymd: string, dow: number): string => {
  const d = parseYmd(ymd);
  let delta = (dow - d.getDay() + 7) % 7;
  if (delta === 0) delta = 7;
  return sumarDias(ymd, delta);
};

export const ymdDe = (iso?: string | null): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return toYMD(d);
};

export const horaDe = (iso?: string | null): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

// ISO de un día local + hora 'HH:MM' (o 09:00)
export const isoLocal = (ymd: string, hhmm?: string | null): string => {
  const [y, m, d] = ymd.split('-').map(Number);
  const [h, mi] = String(hhmm || '09:00').split(':').map(Number);
  return new Date(y, m - 1, d, h || 0, mi || 0, 0, 0).toISOString();
};

export const claveGrupo = (task: TaskItem, hoy: string = hoyLocal()): GrupoKey => {
  if (task.status === 'completed' || task.status === 'cancelled') return 'completadas';
  const ymd = ymdDe(task.dueAt);
  if (!ymd) return 'sinFecha';
  if (ymd < hoy) return 'vencidas';
  if (ymd === hoy) return 'hoy';
  if (ymd === sumarDias(hoy, 1)) return 'manana';
  if (ymd <= finDeSemana(hoy)) return 'semana';
  return 'despues';
};

// "venció ayer", "hoy 15:00", "mañana", "vie 12", "20/09"
export const etiquetaVence = (task: TaskItem, hoy: string = hoyLocal()): string => {
  const ymd = ymdDe(task.dueAt);
  if (!ymd) return '';
  const hora = horaDe(task.dueAt);
  const conHora = (base: string) => (hora && hora !== '00:00' ? `${base} ${hora}` : base);
  const d = parseYmd(ymd);
  if (ymd < hoy) {
    if (ymd === sumarDias(hoy, -1)) return 'venció ayer';
    if (ymd >= sumarDias(hoy, -6)) return `venció el ${DIAS_CORTOS[d.getDay()]} ${d.getDate()}`;
    return `venció el ${d.getDate()}/${String(d.getMonth() + 1).padStart(2, '0')}`;
  }
  if (ymd === hoy) return conHora('hoy');
  if (ymd === sumarDias(hoy, 1)) return conHora('mañana');
  if (ymd <= sumarDias(hoy, 6)) return conHora(`${DIAS_CORTOS[d.getDay()]} ${d.getDate()}`);
  return conHora(`${d.getDate()}/${String(d.getMonth() + 1).padStart(2, '0')}`);
};

export const tituloGrupo = (key: GrupoKey, hoy: string = hoyLocal()): string => {
  const d = parseYmd(hoy);
  if (key === 'hoy') return `Hoy · ${DIAS_LARGOS[d.getDay()]} ${d.getDate()}`;
  if (key === 'manana') {
    const m = parseYmd(sumarDias(hoy, 1));
    return `Mañana · ${DIAS_LARGOS[m.getDay()]} ${m.getDate()}`;
  }
  return GRUPOS.find((g) => g.key === key)?.label || key;
};

const PESO_PRIORIDAD: Record<string, number> = { urgent: 0, high: 1, normal: 2, low: 3 };

export const ordenarTareas = (list: TaskItem[]): TaskItem[] =>
  [...list].sort((a, b) => {
    if (a.status === 'completed' && b.status === 'completed') {
      return String(b.completedAt || '').localeCompare(String(a.completedAt || ''));
    }
    const da = a.dueAt || '9999';
    const db = b.dueAt || '9999';
    if (da !== db) return da.localeCompare(db);
    const pa = PESO_PRIORIDAD[a.priority] ?? 2;
    const pb = PESO_PRIORIDAD[b.priority] ?? 2;
    if (pa !== pb) return pa - pb;
    return String(a.createdAt || '').localeCompare(String(b.createdAt || ''));
  });

export const agruparTareas = (tasks: TaskItem[], hoy: string = hoyLocal()): Record<GrupoKey, TaskItem[]> => {
  const out: Record<GrupoKey, TaskItem[]> = { vencidas: [], hoy: [], manana: [], semana: [], despues: [], sinFecha: [], completadas: [] };
  for (const t of tasks) out[claveGrupo(t, hoy)].push(t);
  (Object.keys(out) as GrupoKey[]).forEach((k) => { out[k] = ordenarTareas(out[k]); });
  return out;
};

// "09:12 · Patricia" para una completada
export const etiquetaCompletada = (task: TaskItem, hoy: string = hoyLocal()): string => {
  const ymd = ymdDe(task.completedAt);
  const hora = horaDe(task.completedAt);
  let cuando = '';
  if (ymd && hora) {
    if (ymd === hoy) cuando = hora;
    else if (ymd === sumarDias(hoy, -1)) cuando = `ayer ${hora}`;
    else { const d = parseYmd(ymd); cuando = `${DIAS_CORTOS[d.getDay()]} ${d.getDate()} ${hora}`; }
  }
  const quien = task.completerName || '';
  return [cuando, quien].filter(Boolean).join(' · ');
};
